<script setup lang="ts">
/**
 * Tabbed code samples. Renders one tab per language and a single highlighted
 * CopyCodeBlock for the active tab, so multi-language examples (Python /
 * TypeScript / curl) take one block's height instead of stacking.
 *
 * The tab label replaces CopyCodeBlock's own `label`, so we pass only
 * `code` + `language` down.
 */
import { ref } from 'vue';
import CopyCodeBlock from './CopyCodeBlock.vue';

interface CodeTab {
  label: string;
  code: string;
  /** highlight.js language id (python | typescript | bash | json). */
  language?: string;
}

const props = defineProps<{ tabs: CodeTab[] }>();
const active = ref(0);
</script>

<template>
  <div>
    <div role="tablist" class="flex flex-wrap gap-1 border-b border-hairline mb-3">
      <button
        v-for="(t, i) in props.tabs"
        :key="t.label"
        type="button"
        role="tab"
        :aria-selected="active === i"
        :class="[
          'px-3 py-1.5 text-sm font-medium -mb-px border-b-2 transition focus-visible:outline-none',
          active === i
            ? 'border-ink text-ink'
            : 'border-transparent text-muted hover:text-ink',
        ]"
        @click="active = i"
      >
        {{ t.label }}
      </button>
    </div>
    <CopyCodeBlock
      :key="active"
      :code="props.tabs[active].code"
      :language="props.tabs[active].language"
    />
  </div>
</template>
